import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription, merge, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError, tap } from 'rxjs/operators';
import { ConfirmationService } from 'primeng/api';
import { LayoutService, CreateLayoutPayload } from './layout.service';
import { ProgressBarService } from '../services/progress-bar.service';
import { MessageHandlerService } from '../services/message-handler.service';
import {
  ERROR_MESSAGES,
  LAYOUT_JSON_FILE_EXTENSION,
  LAYOUT_JSON_INVALID_FILE_TYPE,
  LAYOUT_JSON_INVALID_FORMAT,
  LAYOUT_JSON_EMPTY,
  LAYOUT_JSON_NO_LAYOUTS
} from '../common/Constants';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit, OnDestroy {
  dialogVisible = false;
  isSubmitting = false;
  isEditMode = false;
  selectedLayout: any = null;
  layoutForm: FormGroup | null = null;
  newSubLayoutNames: { [key: number]: string } = {}; // Track new subLayout names per layout index

  // File upload properties
  selectedJsonFile: File | null = null;
  uploadProgressBar = false;
  uploadProgressValue = 0;
  isFileUploaded = false; // Flag to enable/disable form fields
  uploadedFileName: string | null = null;

  // Layout listing properties
  layouts: any[] = [];
  searchInput = '';
  pageNo = 0;
  noOfRecordsPerRow = 10;
  rowsPerPageOptions: number[] = [10, 20, 30];
  totalLayouts = 0;

  private subscriptions: Subscription[] = [];
  private searchSubject = new Subject<string>();
  private refreshSubject = new Subject<void>();

  // Collapsible sections state
  collapsedSections: { [key: string]: boolean } = {
    apps: false,
    layouts: false,
    version: false,
  };

  // Track collapsed state for individual items
  collapsedApps: { [key: number]: boolean } = {};
  collapsedLayouts: { [key: number]: boolean } = {};
  collapsedConditions: { [key: string]: boolean } = {}; // key: "layoutIndex-conditionIndex"
  collapsedPreviousLayouts: { [key: number]: boolean } = {}; // key: layoutIndex
  collapsedSubLayouts: { [key: number]: boolean } = {}; // key: layoutIndex

  // IOHC-1723: workflow ("flows") JSON-editor state, keyed by layout index. Workflow is a
  // heterogeneous/recursive hierarchy (IOHC-998) preserved verbatim, so it is edited as JSON.
  collapsedWorkflow: { [key: number]: boolean } = {};
  workflowJsonText: { [key: number]: string } = {};
  workflowJsonError: { [key: number]: string } = {};

  constructor(
    private readonly layoutService: LayoutService,
    private readonly progressBarService: ProgressBarService,
    private readonly messageHandlerService: MessageHandlerService,
    private readonly confirmationService: ConfirmationService,
    private readonly formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.initSearchAndListPipeline();
    this.getLayouts();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private initSearchAndListPipeline(): void {
    const search$ = this.searchSubject.pipe(debounceTime(1000), distinctUntilChanged());
    const refresh$ = this.refreshSubject.asObservable();

    this.subscriptions.push(
      merge(search$, refresh$)
        .pipe(
          tap(() => {
            this.progressBarService.setProgressBarVisibility(true);
          }),
          switchMap((searchTerm) => {
            const term = typeof searchTerm === 'string' ? searchTerm.trim() : this.searchInput.trim();

            return this.layoutService
              .getAll({
                page: this.pageNo + 1, // API expects 1-based page numbers
                limit: this.noOfRecordsPerRow,
                search: term || undefined,
              })
              .pipe(
                catchError((error) => {
                  return of({
                    success: false,
                    errorMessage: error?.errorMessage || error?.message || error,
                  });
                }),
              );
          }),
        )
        .subscribe((res: any) => {
          if (this.isLayoutListSuccessResponse(res)) {
            // Handle response format: { success: true, data: [...], totalItems: number, ... }
            const rawData = Array.isArray(res.data)
              ? res.data
              : Array.isArray(res.content?.data)
                ? res.content.data
                : [];

            this.layouts = rawData || [];
            // Extract totalItems from pagination info
            this.totalLayouts =
              res.totalItems ??
              res.content?.pagination?.total ??
              res.content?.totalElements ??
              res.total ??
              rawData.length;

            // Pagination safety: if we're on a page that became empty after a delete,
            // step back one page and refetch so the UI updates instead of showing an empty list.
            if (this.layouts.length === 0 && this.totalLayouts > 0 && this.pageNo > 0) {
              this.pageNo = Math.max(0, this.pageNo - 1);
              this.refreshSubject.next();
              return;
            }
          } else {
            this.layouts = [];
            this.totalLayouts = 0;
            if (res?.errorMessage) {
              this.messageHandlerService.generateToastMessage(
                'error',
                ERROR_MESSAGES.COMMUNICATION,
                res.errorMessage,
              );
            }
          }
          this.progressBarService.setProgressBarVisibility(false);
        }),
    );
  }

  private isLayoutListSuccessResponse(res: any): boolean {
    if (res?.success === true) {
      return true;
    }
    if (res?.statusCode >= 200 && res?.statusCode < 300) {
      return true;
    }
    if (res?.content && !res?.errorMessage) {
      return true;
    }
    // Check if response is an array directly
    if (Array.isArray(res)) {
      return true;
    }
    // Check if response has data array
    if (Array.isArray(res?.data)) {
      return true;
    }
    return false;
  }

  getLayouts(): void {
    this.refreshSubject.next();
  }

  onSearchKeyup(): void {
    this.pageNo = 0;
    this.searchSubject.next(this.searchInput.trim());
  }

  paginate(event: any): void {
    this.pageNo = event?.page ?? 0;
    this.noOfRecordsPerRow = event?.rows ?? this.noOfRecordsPerRow;
    this.refreshSubject.next();
  }

  private initForm(): void {
    try {
      this.layoutForm = this.formBuilder.group({
        apps: this.formBuilder.array([this.createAppFormGroup()]),
        layouts: this.formBuilder.array([this.createLayoutFormGroup()]),
        version: [1, [Validators.required, Validators.min(1)]],
        companyId: [''],
        agencyId: [''],
        userId: [''],
      });
      // Version is system-managed; user must not edit it.
      this.layoutForm.get('version')?.disable({ emitEvent: false });
      // Reset collapsed states
      this.collapsedSections = {
        apps: false,
        layouts: false,
        version: false,
      };
      this.collapsedApps = { 0: false }; // First app expanded by default
      this.collapsedLayouts = { 0: false }; // First layout expanded by default
      // All other sections (Conditions, Previous Layouts, Sub-layouts) should be collapsed by default
      this.collapsedConditions = {};
      this.collapsedPreviousLayouts = {};
      this.collapsedSubLayouts = {};
// IOHC-1723: reset the workflow-editor maps too, else a stale workflowJsonError from a
      // prior dialog/session keeps blocking Save (hasWorkflowErrors) with an invisible error.
      this.resetWorkflowEditorState();
      // Explicitly collapse conditions for the first layout
      this.collapsedConditions['conditions-0'] = true;
      this.collapsedConditions['0-0'] = true;
      this.collapsedPreviousLayouts[0] = true;
      this.collapsedSubLayouts[0] = true;
    } catch (error) {
      console.error('Error initializing form:', error);
      this.layoutForm = null;
    }
  }

  private createAppFormGroup(): FormGroup {
    return this.formBuilder.group({
      package: ['', [Validators.required]],
      version: ['', [Validators.required]],
      minVersion: ['', [Validators.required]],
      maxVersion: ['', [Validators.required]],
    });
  }

  private createLayoutFormGroup(): FormGroup {
    const layoutIndex = this.layoutsFormArray.length;
    return this.formBuilder.group({
      layoutType: ['grid', [Validators.required]],
      priority: [1, [Validators.required, Validators.min(1)]],
      critical: [false],
      alertProbability: [0.9, [Validators.required, Validators.min(0), Validators.max(1)]],
      contextType: [''], // SingleScreen, MultiScreen, etc.
      screenContext: [''], // e.g., demographics, contacts, medical_records
      conditions: this.formBuilder.array([this.createConditionFormGroup()]),
      // IOHB-1724: start empty. Injecting a default 'None' made every rule that authored no
      // previousLayouts save as ["None"], which flips the Android detector from "no constraint"
      // to "previous layout must be None" and rejects the rule on most navigations.
      previousLayouts: this.formBuilder.array([]),
      subLayouts: this.formBuilder.group({}), // Will be populated dynamically
      // IOHC-1723: additional layout-rule fields carried through faithfully (IOHC-998 contract).
      previousScreenContexts: [null], // string[] — opaque, edited via comma-separated input
      supportedOverlays: [null], // string[]
      supportedHighlights: [null], // string[]
      workflow: [null], // any[] — workflow "flows", edited via JSON editor
    });
  }

  private createInlineConditionFormGroup(): FormGroup {
    // A non-recursive condition group (used for NOT.condition) to avoid infinite nesting.
    return this.formBuilder.group({
      type: ['CHILD_COUNT', [Validators.required]],
      description: [''],
      min: [null],
      max: [null],
      values: this.formBuilder.array([]),
      index: [null],
      indices: this.formBuilder.array([]),
      exact: [false],
      className: [''],
      classNames: this.formBuilder.array([]),
      regex: [''],
      reverseSearch: [false],
      target: this.formBuilder.array([]),
      conditions: this.formBuilder.array([]),
      item: [''],
      items: this.formBuilder.array([]),
      itemClassName: [''],
      minSize: [null],
      layout: [''],
      readOnly: [false],
      ignoreConditions: this.formBuilder.array([]),
      step: [null], // IOHB-1724: LIST stride
      condition: this.formBuilder.group({}),
      targetConditionMatching: [false],
      toIndex: [null],
      fromIndex: [null],
      childCount: this.formBuilder.group({
        min: [null],
        max: [null],
      }),
      ref: this.formBuilder.group({
        tag: [''],
        index: [null],
        relativeIndex: [null],
        context: [''],
        regex: [''],
        valueRegex: [''],
      }),
    });
  }

  createConditionFormGroup(): FormGroup {
    return this.formBuilder.group({
      type: ['CHILD_COUNT', [Validators.required]], // CHILD_COUNT, CLASS_NAME, TEXT_MATCH, CHILDREN, LIST, LAYOUT, OR, NOT, META
      // Common fields
      description: [''],
      min: [null],
      max: [null],
      // For CLASS_NAME, TEXT_MATCH
      values: this.formBuilder.array([]),
      // For positioning
      index: [null],
      indices: this.formBuilder.array([]),
      // For TEXT_MATCH
      exact: [false],
      className: [''],
      classNames: this.formBuilder.array([]),
      regex: [''],
      reverseSearch: [false],
      // For CHILDREN, LIST
      target: this.formBuilder.array([]),
      conditions: this.formBuilder.array([]), // For CHILDREN, OR
      targetConditionMatching: [false], // For CHILDREN, LIST
      toIndex: [null], // For LIST
      // For LIST
      item: [''],
      items: this.formBuilder.array([]),
      itemClassName: [''],
      minSize: [null],
      // For LAYOUT
      layout: [''],
      readOnly: [false],
      ignoreConditions: this.formBuilder.array([]),
      step: [null], // IOHB-1724: LIST stride
      // For NOT, OR
      condition: this.createInlineConditionFormGroup(), // For NOT
      // For META
      fromIndex: [null],
      childCount: this.formBuilder.group({
        min: [null],
        max: [null],
      }),
      // For ref
      ref: this.formBuilder.group({
        tag: [''],
        index: [null],
        relativeIndex: [null],
        context: [''],
        regex: [''],
        valueRegex: [''],
      }),
    });
  }

  get appsFormArray(): FormArray {
    if (!this.layoutForm) {
      return this.formBuilder.array([]) as FormArray;
    }
    return this.layoutForm.get('apps') as FormArray;
  }

  get layoutsFormArray(): FormArray {
    if (!this.layoutForm) {
      return this.formBuilder.array([]) as FormArray;
    }
    return this.layoutForm.get('layouts') as FormArray;
  }

  getConditionsFormArray(layoutIndex: number): FormArray {
    if (!this.layoutForm || !this.layoutsFormArray.length) {
      return this.formBuilder.array([]) as FormArray;
    }
    const layout = this.layoutsFormArray.at(layoutIndex);
    return layout ? (layout.get('conditions') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getPreviousLayoutsFormArray(layoutIndex: number): FormArray {
    if (!this.layoutForm || !this.layoutsFormArray.length) {
      return this.formBuilder.array([]) as FormArray;
    }
    const layout = this.layoutsFormArray.at(layoutIndex);
    return layout ? (layout.get('previousLayouts') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getSubLayoutsFormGroup(layoutIndex: number): FormGroup {
    if (!this.layoutForm || !this.layoutsFormArray.length) {
      return this.formBuilder.group({});
    }
    const layout = this.layoutsFormArray.at(layoutIndex);
    return layout ? (layout.get('subLayouts') as FormGroup) : this.formBuilder.group({});
  }

  // Helper methods for condition form arrays
  getConditionValuesArray(layoutIndex: number, conditionIndex: number): FormArray {
    const condition = this.getConditionsFormArray(layoutIndex).at(conditionIndex);
    return condition ? (condition.get('values') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getConditionIndicesArray(layoutIndex: number, conditionIndex: number): FormArray {
    const condition = this.getConditionsFormArray(layoutIndex).at(conditionIndex);
    return condition ? (condition.get('indices') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getConditionClassNamesArray(layoutIndex: number, conditionIndex: number): FormArray {
    const condition = this.getConditionsFormArray(layoutIndex).at(conditionIndex);
    return condition ? (condition.get('classNames') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getConditionTargetArray(layoutIndex: number, conditionIndex: number): FormArray {
    const condition = this.getConditionsFormArray(layoutIndex).at(conditionIndex);
    return condition ? (condition.get('target') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getConditionConditionsArray(layoutIndex: number, conditionIndex: number): FormArray {
    const condition = this.getConditionsFormArray(layoutIndex).at(conditionIndex);
    return condition ? (condition.get('conditions') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getConditionItemsArray(layoutIndex: number, conditionIndex: number): FormArray {
    const condition = this.getConditionsFormArray(layoutIndex).at(conditionIndex);
    return condition ? (condition.get('items') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getConditionIgnoreConditionsArray(layoutIndex: number, conditionIndex: number): FormArray {
    const condition = this.getConditionsFormArray(layoutIndex).at(conditionIndex);
    return condition ? (condition.get('ignoreConditions') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getConditionRefFormGroup(layoutIndex: number, conditionIndex: number): FormGroup {
    const condition = this.getConditionsFormArray(layoutIndex).at(conditionIndex);
    return condition ? (condition.get('ref') as FormGroup) : this.formBuilder.group({});
  }

  getConditionConditionFormGroup(layoutIndex: number, conditionIndex: number): FormGroup {
    const condition = this.getConditionsFormArray(layoutIndex).at(conditionIndex);
    return condition ? (condition.get('condition') as FormGroup) : this.formBuilder.group({});
  }

  getNotConditionFormGroup(layoutIndex: number, conditionIndex: number): FormGroup {
    return this.getConditionConditionFormGroup(layoutIndex, conditionIndex);
  }

  getNotConditionRefFormGroup(layoutIndex: number, conditionIndex: number): FormGroup {
    const notGroup = this.getNotConditionFormGroup(layoutIndex, conditionIndex);
    return notGroup ? (notGroup.get('ref') as FormGroup) : this.formBuilder.group({});
  }

  getNotConditionValuesArray(layoutIndex: number, conditionIndex: number): FormArray {
    const notGroup = this.getNotConditionFormGroup(layoutIndex, conditionIndex);
    return notGroup ? (notGroup.get('values') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getNotConditionIndicesArray(layoutIndex: number, conditionIndex: number): FormArray {
    const notGroup = this.getNotConditionFormGroup(layoutIndex, conditionIndex);
    return notGroup ? (notGroup.get('indices') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getNotConditionClassNamesArray(layoutIndex: number, conditionIndex: number): FormArray {
    const notGroup = this.getNotConditionFormGroup(layoutIndex, conditionIndex);
    return notGroup ? (notGroup.get('classNames') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  addNotConditionValue(layoutIndex: number, conditionIndex: number): void {
    this.getNotConditionValuesArray(layoutIndex, conditionIndex).push(this.formBuilder.control(''));
  }

  removeNotConditionValue(layoutIndex: number, conditionIndex: number, valueIndex: number): void {
    this.getNotConditionValuesArray(layoutIndex, conditionIndex).removeAt(valueIndex);
  }

  addNotConditionIndex(layoutIndex: number, conditionIndex: number): void {
    this.getNotConditionIndicesArray(layoutIndex, conditionIndex).push(this.formBuilder.control(null));
  }

  removeNotConditionIndex(layoutIndex: number, conditionIndex: number, index: number): void {
    this.getNotConditionIndicesArray(layoutIndex, conditionIndex).removeAt(index);
  }

  addNotConditionClassName(layoutIndex: number, conditionIndex: number): void {
    this.getNotConditionClassNamesArray(layoutIndex, conditionIndex).push(this.formBuilder.control(''));
  }

  removeNotConditionClassName(layoutIndex: number, conditionIndex: number, classNameIndex: number): void {
    this.getNotConditionClassNamesArray(layoutIndex, conditionIndex).removeAt(classNameIndex);
  }

  // Methods to add/remove values from arrays
  addConditionValue(layoutIndex: number, conditionIndex: number): void {
    this.getConditionValuesArray(layoutIndex, conditionIndex).push(this.formBuilder.control(''));
  }

  removeConditionValue(layoutIndex: number, conditionIndex: number, valueIndex: number): void {
    this.getConditionValuesArray(layoutIndex, conditionIndex).removeAt(valueIndex);
  }

  addConditionIndex(layoutIndex: number, conditionIndex: number): void {
    this.getConditionIndicesArray(layoutIndex, conditionIndex).push(this.formBuilder.control(null));
  }

  removeConditionIndex(layoutIndex: number, conditionIndex: number, index: number): void {
    this.getConditionIndicesArray(layoutIndex, conditionIndex).removeAt(index);
  }

  addConditionClassName(layoutIndex: number, conditionIndex: number): void {
    this.getConditionClassNamesArray(layoutIndex, conditionIndex).push(this.formBuilder.control(''));
  }

  removeConditionClassName(layoutIndex: number, conditionIndex: number, classNameIndex: number): void {
    this.getConditionClassNamesArray(layoutIndex, conditionIndex).removeAt(classNameIndex);
  }

  addNestedCondition(layoutIndex: number, conditionIndex: number): void {
    this.getConditionConditionsArray(layoutIndex, conditionIndex).push(this.createConditionFormGroup());
  }

  removeNestedCondition(layoutIndex: number, conditionIndex: number, nestedIndex: number): void {
    this.getConditionConditionsArray(layoutIndex, conditionIndex).removeAt(nestedIndex);
  }

  addSubLayout(layoutIndex: number, subLayoutName: string): void {
    const subLayoutsGroup = this.getSubLayoutsFormGroup(layoutIndex);
    if (!subLayoutsGroup.get(subLayoutName)) {
      subLayoutsGroup.addControl(subLayoutName, this.formBuilder.array([this.createConditionFormGroup()]));
    }
  }

  removeSubLayout(layoutIndex: number, subLayoutName: string): void {
    const subLayoutsGroup = this.getSubLayoutsFormGroup(layoutIndex);
    subLayoutsGroup.removeControl(subLayoutName);
  }

  getSubLayoutFormArray(layoutIndex: number, subLayoutName: string): FormArray {
    const subLayoutsGroup = this.getSubLayoutsFormGroup(layoutIndex);
    return subLayoutsGroup.get(subLayoutName) as FormArray || this.formBuilder.array([]) as FormArray;
  }

  getSubLayoutNames(layoutIndex: number): string[] {
    const subLayoutsGroup = this.getSubLayoutsFormGroup(layoutIndex);
    return Object.keys(subLayoutsGroup.controls);
  }

  toggleSubLayouts(layoutIndex: number): void {
    if (this.isBusy) return;
    this.collapsedSubLayouts[layoutIndex] = !this.collapsedSubLayouts[layoutIndex];
  }

  isSubLayoutsCollapsed(layoutIndex: number): boolean {
    return this.collapsedSubLayouts[layoutIndex] === true;
  }

  addSubLayoutInline(layoutIndex: number, subLayoutName: string): void {
    if (subLayoutName?.trim()) {
      this.addSubLayout(layoutIndex, subLayoutName.trim());
      this.newSubLayoutNames[layoutIndex] = '';
    }
  }

  onSubLayoutInputChange(layoutIndex: number): void {
    // This method is called on input change to trigger change detection for button state
    // Force change detection to update button state
    if (!this.layoutForm) return;
    // The button disabled state is already handled by the template binding
    // This ensures Angular detects the change in newSubLayoutNames
    // Trigger change detection
    setTimeout(() => {
      // Force change detection
    }, 0);
  }

  isSubLayoutAddButtonEnabled(layoutIndex: number): boolean {
    const value = this.newSubLayoutNames[layoutIndex]?.trim();
    return !!value;
  }

  removeSubLayoutCondition(layoutIndex: number, subLayoutName: string, conditionIndex: number): void {
    const subLayoutArray = this.getSubLayoutFormArray(layoutIndex, subLayoutName);
    if (subLayoutArray.length > 0) {
      subLayoutArray.removeAt(conditionIndex);
    }
  }

  createFormControl(value: any = ''): any {
    return this.formBuilder.control(value);
  }

  // Helper methods for sub-layout condition fields
  getSubLayoutConditionValuesArray(layoutIndex: number, subLayoutName: string, conditionIndex: number): FormArray {
    const condition = this.getSubLayoutFormArray(layoutIndex, subLayoutName).at(conditionIndex);
    return condition ? (condition.get('values') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getSubLayoutConditionClassNamesArray(layoutIndex: number, subLayoutName: string, conditionIndex: number): FormArray {
    const condition = this.getSubLayoutFormArray(layoutIndex, subLayoutName).at(conditionIndex);
    return condition ? (condition.get('classNames') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getSubLayoutConditionRefFormGroup(layoutIndex: number, subLayoutName: string, conditionIndex: number): FormGroup {
    const condition = this.getSubLayoutFormArray(layoutIndex, subLayoutName).at(conditionIndex);
    return condition ? (condition.get('ref') as FormGroup) : this.formBuilder.group({});
  }

  getSubLayoutConditionChildCountFormGroup(layoutIndex: number, subLayoutName: string, conditionIndex: number): FormGroup {
    const condition = this.getSubLayoutFormArray(layoutIndex, subLayoutName).at(conditionIndex);
    return condition ? (condition.get('childCount') as FormGroup) : this.formBuilder.group({ min: [null], max: [null] });
  }

  addSubLayoutConditionValue(layoutIndex: number, subLayoutName: string, conditionIndex: number): void {
    this.getSubLayoutConditionValuesArray(layoutIndex, subLayoutName, conditionIndex).push(this.formBuilder.control(''));
  }

  removeSubLayoutConditionValue(layoutIndex: number, subLayoutName: string, conditionIndex: number, valueIndex: number): void {
    this.getSubLayoutConditionValuesArray(layoutIndex, subLayoutName, conditionIndex).removeAt(valueIndex);
  }

  addSubLayoutConditionClassName(layoutIndex: number, subLayoutName: string, conditionIndex: number): void {
    this.getSubLayoutConditionClassNamesArray(layoutIndex, subLayoutName, conditionIndex).push(this.formBuilder.control(''));
  }

  removeSubLayoutConditionClassName(layoutIndex: number, subLayoutName: string, conditionIndex: number, classNameIndex: number): void {
    this.getSubLayoutConditionClassNamesArray(layoutIndex, subLayoutName, conditionIndex).removeAt(classNameIndex);
  }

  // Helper methods for target conditions
  getTargetValuesArray(layoutIndex: number, conditionIndex: number, targetIndex: number): FormArray {
    const target = this.getConditionTargetArray(layoutIndex, conditionIndex).at(targetIndex);
    return target ? (target.get('values') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getTargetRefFormGroup(layoutIndex: number, conditionIndex: number, targetIndex: number): FormGroup {
    const target = this.getConditionTargetArray(layoutIndex, conditionIndex).at(targetIndex);
    return target ? (target.get('ref') as FormGroup) : this.formBuilder.group({});
  }

  addTargetValue(layoutIndex: number, conditionIndex: number, targetIndex: number): void {
    this.getTargetValuesArray(layoutIndex, conditionIndex, targetIndex).push(this.formBuilder.control(''));
  }

  removeTargetValue(layoutIndex: number, conditionIndex: number, targetIndex: number, valueIndex: number): void {
    this.getTargetValuesArray(layoutIndex, conditionIndex, targetIndex).removeAt(valueIndex);
  }

  getTargetIndicesArray(layoutIndex: number, conditionIndex: number, targetIndex: number): FormArray {
    const target = this.getConditionTargetArray(layoutIndex, conditionIndex).at(targetIndex);
    return target ? (target.get('indices') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  addTargetIndex(layoutIndex: number, conditionIndex: number, targetIndex: number): void {
    this.getTargetIndicesArray(layoutIndex, conditionIndex, targetIndex).push(this.formBuilder.control(null));
  }

  removeTargetIndex(layoutIndex: number, conditionIndex: number, targetIndex: number, indexPos: number): void {
    this.getTargetIndicesArray(layoutIndex, conditionIndex, targetIndex).removeAt(indexPos);
  }

  // Helper methods for nested conditions
  getNestedConditionValuesArray(layoutIndex: number, conditionIndex: number, nestedIndex: number): FormArray {
    const nestedCondition = this.getConditionConditionsArray(layoutIndex, conditionIndex).at(nestedIndex);
    return nestedCondition ? (nestedCondition.get('values') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getNestedConditionRefFormGroup(layoutIndex: number, conditionIndex: number, nestedIndex: number): FormGroup {
    const nestedCondition = this.getConditionConditionsArray(layoutIndex, conditionIndex).at(nestedIndex);
    return nestedCondition ? (nestedCondition.get('ref') as FormGroup) : this.formBuilder.group({});
  }

  addNestedConditionValue(layoutIndex: number, conditionIndex: number, nestedIndex: number): void {
    this.getNestedConditionValuesArray(layoutIndex, conditionIndex, nestedIndex).push(this.formBuilder.control(''));
  }

  removeNestedConditionValue(layoutIndex: number, conditionIndex: number, nestedIndex: number, valueIndex: number): void {
    this.getNestedConditionValuesArray(layoutIndex, conditionIndex, nestedIndex).removeAt(valueIndex);
  }

  addApp(): void {
    const index = this.appsFormArray.length;
    this.appsFormArray.push(this.createAppFormGroup());
    this.collapsedApps[index] = true; // Default to collapsed for new apps
  }

  removeApp(index: number): void {
    if (this.appsFormArray.length > 1) {
      this.appsFormArray.removeAt(index);
    }
  }

  addLayout(): void {
    const index = this.layoutsFormArray.length;
    this.layoutsFormArray.push(this.createLayoutFormGroup());
    this.collapsedLayouts[index] = true; // Default to collapsed
    this.collapsedPreviousLayouts[index] = true; // Default to collapsed
    this.collapsedSubLayouts[index] = true; // Default to collapsed
    // Initialize conditions section collapsed state
    const conditionsKey = `conditions-${index}`;
    this.collapsedConditions[conditionsKey] = true; // Default to collapsed
    // Initialize first condition collapsed state
    const firstConditionKey = `${index}-0`;
    this.collapsedConditions[firstConditionKey] = true; // Default to collapsed
  }

  removeLayout(index: number): void {
    if (this.layoutsFormArray.length > 1) {
      this.layoutsFormArray.removeAt(index);
      // FormArray reindexes on removeAt, so shift the index-keyed workflow-editor maps down to
      // stay aligned — otherwise the surviving layout would show the removed layout's JSON and a
      // stale error could keep blocking Save (IOHC-1723).
      const count = this.layoutsFormArray.length; // length AFTER removal
      const shift = (map: { [key: number]: any }) => {
        for (let i = index; i < count; i++) {
          map[i] = map[i + 1];
        }
        delete map[count];
      };
      shift(this.collapsedWorkflow);
      shift(this.workflowJsonText);
      shift(this.workflowJsonError);
    }
  }

  addCondition(layoutIndex: number): void {
    const conditionsArray = this.getConditionsFormArray(layoutIndex);
    const conditionIndex = conditionsArray.length;
    conditionsArray.push(this.createConditionFormGroup());
    const key = `${layoutIndex}-${conditionIndex}`;
    this.collapsedConditions[key] = false; // Default to expanded
  }

  removeCondition(layoutIndex: number, conditionIndex: number): void {
    const conditionsArray = this.getConditionsFormArray(layoutIndex);
    if (conditionsArray.length > 1) {
      conditionsArray.removeAt(conditionIndex);
    }
  }

  addPreviousLayout(layoutIndex: number): void {
    const previousLayoutsArray = this.getPreviousLayoutsFormArray(layoutIndex);
    previousLayoutsArray.push(this.formBuilder.control('None', [Validators.required]));
  }

  removePreviousLayout(layoutIndex: number, previousLayoutIndex: number): void {
    this.getPreviousLayoutsFormArray(layoutIndex).removeAt(previousLayoutIndex);
  }

  openAddDialog(): void {
    try {
      this.isEditMode = false;
      this.selectedLayout = null;
      if (!this.layoutForm) {
        this.initForm();
      }
      // Reset file upload state when opening dialog
      this.selectedJsonFile = null;
      this.uploadedFileName = null;
      this.isFileUploaded = false;
      this.resetUploadState();
      console.log('Opening dialog, layoutForm:', this.layoutForm);
      this.dialogVisible = true;
    } catch (error) {
      console.error('Error opening dialog:', error);
      this.messageHandlerService.generateToastMessage(
        'error',
        'Error',
        'Failed to open form. Please try again.'
      );
    }
  }

  onDialogHide(): void {
    this.dialogVisible = false;
    this.isEditMode = false;
    this.selectedLayout = null;
    // Reset file upload state when closing dialog
    this.selectedJsonFile = null;
    this.uploadedFileName = null;
    this.isFileUploaded = false;
    this.resetUploadState();
    this.initForm();
  }

  onSubmit(): void {
    this.isSubmitting = true;
    this.markFormGroupTouched(this.layoutForm);

    if (!this.layoutForm.valid) {
      this.isSubmitting = false;
      const missingFields = this.getMissingRequiredFields(this.layoutForm);
      const errorMessage = missingFields.length > 0
        ? `Please fill in the following required fields: ${missingFields.join(', ')}`
        : 'Please fill in all required fields correctly.';
      this.messageHandlerService.generateToastMessage(
        'error',
        'Validation Error',
        errorMessage
      );
      return;
    }

    // IOHC-1723: block save while any layout's workflow JSON is invalid.
    if (this.hasWorkflowErrors()) {
      this.isSubmitting = false;
      this.messageHandlerService.generateToastMessage(
        'error',
        'Validation Error',
        'Please fix the workflow JSON error(s) before saving.'
      );
      return;
    }

    this.progressBarService.setProgressBarVisibility(true);

    const formValue = this.layoutForm.getRawValue();
    const payload: CreateLayoutPayload = {
      apps: formValue.apps.map((app: any) => ({
        package: app.package,
        version: app.version,
        minVersion: app.minVersion,
        maxVersion: app.maxVersion,
      })),
      layouts: formValue.layouts.map((layout: any) => ({
        layoutType: layout.layoutType,
        priority: Number(layout.priority),
        critical: Boolean(layout.critical),
        alertProbability: Number(layout.alertProbability),
        contextType: layout.contextType?.trim() || undefined,
        screenContext: layout.screenContext?.trim() || undefined,
        conditions: this.serializeConditions(layout.conditions || []),
        // IOHB-1724: omit when empty (Android treats an absent list as "no constraint";
        // a present list is enforced, so [] / ["None"] would reject the rule).
        previousLayouts: this.cleanStringArray(layout.previousLayouts),
        subLayouts: this.serializeSubLayouts(layout.subLayouts || {}),
        previousScreenContexts: this.cleanStringArray(layout.previousScreenContexts),
        supportedOverlays: this.cleanStringArray(layout.supportedOverlays),
        supportedHighlights: this.cleanStringArray(layout.supportedHighlights),
        workflow: Array.isArray(layout.workflow) && layout.workflow.length > 0 ? layout.workflow : undefined,
      })),
      version: Number(formValue.version ?? 1),
      companyId: formValue.companyId?.trim() || undefined,
      agencyId: formValue.agencyId?.trim() || undefined,
      userId: formValue.userId?.trim() || undefined,
    };

    if (this.isEditMode && this.selectedLayout) {
      // Handle update
      const layoutId = this.selectedLayout._id || this.selectedLayout.id;
      if (!layoutId) {
        this.isSubmitting = false;
        this.progressBarService.setProgressBarVisibility(false);
        this.messageHandlerService.generateToastMessage(
          'error',
          'Validation Error',
          'Layout ID is missing. Cannot update layout.'
        );
        return;
      }

      this.layoutService.updateLayout(layoutId, payload).subscribe({
        next: (response) => {
          // Handle null response from error interceptor (400/403/500 errors)
          if (response === null) {
            this.isSubmitting = false;
            this.progressBarService.setProgressBarVisibility(false);
            return;
          }

          this.progressBarService.setProgressBarVisibility(false);
          const isSuccess = this.isLayoutSuccessResponse(response);

          if (isSuccess) {
            this.messageHandlerService.generateToastMessage(
              'success',
              'Success',
              'Layout updated successfully!'
            );
            this.onDialogHide();
            this.getLayouts(); // Refresh the layout list
          } else {
            this.messageHandlerService.generateToastMessage(
              'error',
              ERROR_MESSAGES.COMMUNICATION,
              response?.errorMessage || 'Failed to update layout'
            );
          }
          this.isSubmitting = false;
        },
        error: (error) => {
          const errorMessage = error?.errorMessage || error?.message || error;

          if (
            typeof errorMessage === 'string' &&
            errorMessage.includes("You provided 'null' where a stream was expected")
          ) {
            this.isSubmitting = false;
            this.progressBarService.setProgressBarVisibility(false);
            return;
          }

          if (error?.status === 403 || error?.status === 400 || error?.status === 500) {
            this.isSubmitting = false;
            this.progressBarService.setProgressBarVisibility(false);
            return;
          }

          this.progressBarService.setProgressBarVisibility(false);
          this.messageHandlerService.generateToastMessage(
            'error',
            ERROR_MESSAGES.COMMUNICATION,
            errorMessage
          );
          this.isSubmitting = false;
        },
      });
    } else {
      // Handle create
      this.layoutService.createLayout(payload).subscribe({
        next: (response) => {
          // Handle null response from error interceptor (400/403/500 errors)
          if (response === null) {
            this.isSubmitting = false;
            this.progressBarService.setProgressBarVisibility(false);
            return;
          }

          this.progressBarService.setProgressBarVisibility(false);
          // Check if response is successful - API returns the created layout object directly
          // Success indicators: has _id, or has success: true, or has layouts array
          const isSuccess = this.isLayoutSuccessResponse(response);

          if (isSuccess) {
            this.messageHandlerService.generateToastMessage(
              'success',
              'Success',
              'Layout created successfully!'
            );
            this.onDialogHide();
            this.getLayouts(); // Refresh the layout list
          } else {
            this.messageHandlerService.generateToastMessage(
              'error',
              ERROR_MESSAGES.COMMUNICATION,
              response?.errorMessage || 'Failed to create layout'
            );
          }
          this.isSubmitting = false;
        },
        error: (error) => {
          const errorMessage = error?.errorMessage || error?.message || error;

          // Swallow technical RxJS error caused by interceptor returning `null`
          // ("You provided 'null' where a stream was expected"), since the
          // ErrorInterceptor already showed a user-facing toast for that case.
          if (
            typeof errorMessage === 'string' &&
            errorMessage.includes("You provided 'null' where a stream was expected")
          ) {
            this.isSubmitting = false;
            this.progressBarService.setProgressBarVisibility(false);
            return;
          }

          // Check if error status is 403/400/500 - interceptor already handled it
          if (error?.status === 403 || error?.status === 400 || error?.status === 500) {
            this.isSubmitting = false;
            this.progressBarService.setProgressBarVisibility(false);
            return;
          }

          this.progressBarService.setProgressBarVisibility(false);
          this.messageHandlerService.generateToastMessage(
            'error',
            ERROR_MESSAGES.COMMUNICATION,
            errorMessage
          );
          this.isSubmitting = false;
        },
      });
    }
  }

  // File upload methods
  onJsonFileSelected(event: any): void {
    if (this.isBusy) {
      return;
    }

    const file: File | undefined = event?.target?.files?.[0];
    if (!file) {
      return;
    }

    // Validate file type
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith(LAYOUT_JSON_FILE_EXTENSION)) {
      this.messageHandlerService.generateToastMessage(
        'error',
        'Invalid File Type',
        LAYOUT_JSON_INVALID_FILE_TYPE
      );
      return;
    }

    this.selectedJsonFile = file;
    this.uploadedFileName = file.name;
    this.uploadProgressBar = true;
    this.uploadProgressValue = 0;
    this.progressBarService.setProgressBarVisibility(true);

    // Simulate progress
    const progressInterval = setInterval(() => {
      if (this.uploadProgressValue < 90) {
        this.uploadProgressValue = this.uploadProgressValue + Math.floor(Math.random() * 15) + 5;
      }
      if (this.uploadProgressValue >= 90 && this.uploadProgressValue < 100) {
        this.uploadProgressValue = 90;
      }
    }, 200);

    // Read and parse JSON file
    const fileReader = new FileReader();
    fileReader.onload = (e: any) => {
      try {
        const jsonContent = e.target?.result;
        if (!jsonContent || jsonContent.trim() === '') {
          clearInterval(progressInterval);
          this.resetUploadState();
          this.messageHandlerService.generateToastMessage(
            'error',
            'Error',
            LAYOUT_JSON_EMPTY
          );
          return;
        }

        const jsonData = JSON.parse(jsonContent);
        clearInterval(progressInterval);
        this.uploadProgressValue = 100;

        // Process JSON data
        setTimeout(() => {
          this.populateFormFromJson(jsonData);
          this.uploadProgressBar = false;
          this.uploadProgressValue = 0;
          this.progressBarService.setProgressBarVisibility(false);
        }, 500);
      } catch (error) {
        clearInterval(progressInterval);
        this.resetUploadState();
        this.messageHandlerService.generateToastMessage(
          'error',
          'Error',
          LAYOUT_JSON_INVALID_FORMAT
        );
      }
    };

    fileReader.onerror = () => {
      clearInterval(progressInterval);
      this.resetUploadState();
      this.messageHandlerService.generateToastMessage(
        'error',
        ERROR_MESSAGES.COMMUNICATION,
        'Failed to read file'
      );
    };

    fileReader.readAsText(file);
  }

  clearSelectedFile(): void {
    if (this.isBusy) {
      return;
    }

    this.selectedJsonFile = null;
    this.uploadedFileName = null;
    this.isFileUploaded = false;
    this.resetUploadState();
    this.initForm();
  }

  private resetUploadState(): void {
    this.uploadProgressBar = false;
    this.uploadProgressValue = 0;
    this.selectedJsonFile = null;
    this.uploadedFileName = null;
    this.progressBarService.setProgressBarVisibility(false);
  }

  private populateFormFromJson(jsonData: any): void {
    try {
      if (!this.layoutForm) {
        this.initForm();
      }

      if (!this.layoutForm) {
        return;
      }

      // Handle different JSON structures
      let layoutsArray: any[] = [];
      let appsArray: any[] = [];
      let version = 1;
      let companyId = '';
      let agencyId = '';
      let userId = '';

      // If JSON is an object with layouts/apps properties
      if (jsonData && typeof jsonData === 'object' && !Array.isArray(jsonData)) {
        layoutsArray = Array.isArray(jsonData.layouts) ? jsonData.layouts : [];
        appsArray = Array.isArray(jsonData.apps) ? jsonData.apps : [];
        version = jsonData.version || 1;
        companyId = jsonData.companyId || '';
        agencyId = jsonData.agencyId || '';
        userId = jsonData.userId || '';
      }
      // If JSON is directly an array of layouts
      else if (Array.isArray(jsonData)) {
        layoutsArray = jsonData;
      }
      // If JSON has a root property that's an array
      else if (jsonData && jsonData.data && Array.isArray(jsonData.data)) {
        layoutsArray = jsonData.data;
      }

      // Validate layouts array
      if (!Array.isArray(layoutsArray) || layoutsArray.length === 0) {
        this.messageHandlerService.generateToastMessage(
          'warn',
          'Warning',
          LAYOUT_JSON_NO_LAYOUTS
        );
        return;
      }

      // Clear existing form arrays
      this.resetWorkflowEditorState(); // IOHC-1723: drop stale per-index workflow-editor state
      const appsFormArray = this.appsFormArray;
      const layoutsFormArray = this.layoutsFormArray;

      while (appsFormArray.length > 0) {
        appsFormArray.removeAt(0);
      }
      while (layoutsFormArray.length > 0) {
        layoutsFormArray.removeAt(0);
      }

      // Populate apps
      if (appsArray.length > 0) {
        appsArray.forEach((app, index) => {
          appsFormArray.push(this.formBuilder.group({
            package: [app.package || '', [Validators.required]],
            version: [app.version || '', [Validators.required]],
            minVersion: [app.minVersion || '', [Validators.required]],
            maxVersion: [app.maxVersion || '', [Validators.required]],
          }));
          this.collapsedApps[index] = index !== 0; // Only first app expanded
        });
      } else {
        // Add at least one empty app
        appsFormArray.push(this.createAppFormGroup());
        this.collapsedApps[0] = false;
      }

      // Populate layouts
      layoutsArray.forEach((layout, index) => {
        const layoutGroup = this.createLayoutFormGroup();

        // ... (rest of the mapping logic remains the same)

        // Set basic properties
        layoutGroup.patchValue({
          layoutType: layout.layoutType || 'grid',
          priority: layout.priority || 1,
          critical: layout.critical || false,
          alertProbability: layout.alertProbability || 0.9,
          contextType: layout.contextType || '',
          screenContext: layout.screenContext || '',
        });

        // Populate conditions
        const conditionsArray = layoutGroup.get('conditions') as FormArray;
        while (conditionsArray.length > 0) {
          conditionsArray.removeAt(0);
        }
        if (Array.isArray(layout.conditions) && layout.conditions.length > 0) {
          layout.conditions.forEach((condition: any) => {
            conditionsArray.push(this.mapJsonConditionToForm(condition));
          });
        }

        this.populatePreviousLayouts(layoutGroup, layout);

        // Populate subLayouts
        const subLayoutsGroup = layoutGroup.get('subLayouts') as FormGroup;
        if (layout.subLayouts && typeof layout.subLayouts === 'object') {
          Object.keys(layout.subLayouts).forEach((subLayoutName) => {
            const subLayoutConditions = layout.subLayouts[subLayoutName];
            if (Array.isArray(subLayoutConditions)) {
              const conditionFormGroups = subLayoutConditions.map((condition: any) =>
                this.mapJsonConditionToForm(condition)
              );
              const subLayoutArray = this.formBuilder.array(conditionFormGroups);
              subLayoutsGroup.addControl(subLayoutName, subLayoutArray);
            }
          });
        }

        // IOHC-1723: carry-through fields + workflow ("flows"), preserved verbatim.
        this.applyCarryThroughFields(layoutGroup, layout, index);

        layoutsFormArray.push(layoutGroup);

        // Initialize collapsed states for new layout
        this.collapsedLayouts[index] = index !== 0; // Only first layout expanded
        this.collapsedPreviousLayouts[index] = true; // Related sections collapsed by default
        this.collapsedSubLayouts[index] = true; // Related sections collapsed by default
        const conditionsKey = `conditions-${index}`;
        this.collapsedConditions[conditionsKey] = true; // Related sections collapsed by default
      });

      // Set root level properties
      this.layoutForm.patchValue({
        version: version,
        companyId: companyId,
        agencyId: agencyId,
        userId: userId,
      });

      // Collapse top-level sections except Apps and Layouts
      this.collapsedSections = {
        apps: false,
        layouts: false,
        version: false,
      };

      // Enable form fields
      this.isFileUploaded = true;

      this.messageHandlerService.generateToastMessage(
        'success',
        'Success',
        `Successfully loaded ${layoutsArray.length} layout(s) from JSON file`
      );
    } catch (error) {
      console.error('Error populating form from JSON:', error);
      this.messageHandlerService.generateToastMessage(
        'error',
        'Error',
        'Failed to populate form from JSON file'
      );
    }
  }

  private mapJsonConditionToForm(condition: any): FormGroup {
    const conditionGroup = this.createConditionFormGroup();
    // Set the type after creating the group
    conditionGroup.patchValue({
      type: condition.type || 'CLASS_NAME'
    });

    // Set basic properties
    conditionGroup.patchValue({
      type: condition.type || 'CLASS_NAME',
      description: condition.description || '',
      min: condition.min !== undefined ? condition.min : null,
      max: condition.max !== undefined ? condition.max : null,
      exact: condition.exact || false,
      className: condition.className || '',
      regex: condition.regex || '',
      reverseSearch: condition.reverseSearch || false,
      index: condition.index !== undefined ? condition.index : null,
      item: condition.item || '',
      itemClassName: condition.itemClassName || '',
      minSize: condition.minSize !== undefined ? condition.minSize : null,
      step: condition.step !== undefined ? condition.step : null,
      layout: condition.layout || '',
      readOnly: condition.readOnly || false,
      targetConditionMatching: condition.targetConditionMatching || false,
      toIndex: condition.toIndex !== undefined ? condition.toIndex : null,
      fromIndex: condition.fromIndex !== undefined ? condition.fromIndex : null,
    });

    // Map childCount object (for META)
    if (condition.childCount && typeof condition.childCount === 'object') {
      const childCountGroup = conditionGroup.get('childCount') as FormGroup;
      childCountGroup.patchValue({
        min: condition.childCount.min !== undefined ? condition.childCount.min : null,
        max: condition.childCount.max !== undefined ? condition.childCount.max : null,
      });
    }

    // Map arrays
    if (Array.isArray(condition.values)) {
      const valuesArray = conditionGroup.get('values') as FormArray;
      condition.values.forEach((value: string) => {
        valuesArray.push(this.formBuilder.control(value || ''));
      });
    }

    if (Array.isArray(condition.indices)) {
      const indicesArray = conditionGroup.get('indices') as FormArray;
      condition.indices.forEach((index: number) => {
        indicesArray.push(this.formBuilder.control(index !== undefined ? index : null));
      });
    }

    if (Array.isArray(condition.classNames)) {
      const classNamesArray = conditionGroup.get('classNames') as FormArray;
      condition.classNames.forEach((className: string) => {
        classNamesArray.push(this.formBuilder.control(className || ''));
      });
    }

    if (Array.isArray(condition.items)) {
      const itemsArray = conditionGroup.get('items') as FormArray;
      condition.items.forEach((item: string) => {
        itemsArray.push(this.formBuilder.control(item || ''));
      });
    }

    if (Array.isArray(condition.ignoreConditions)) {
      const ignoreConditionsArray = conditionGroup.get('ignoreConditions') as FormArray;
      condition.ignoreConditions.forEach((ignoreIndex: number) => {
        ignoreConditionsArray.push(this.formBuilder.control(ignoreIndex !== undefined ? ignoreIndex : null));
      });
    }

    // Map ref object
    if (condition.ref && typeof condition.ref === 'object') {
      const refGroup = conditionGroup.get('ref') as FormGroup;
      refGroup.patchValue({
        tag: condition.ref.tag || '',
        index: condition.ref.index !== undefined ? condition.ref.index : null,
        relativeIndex: condition.ref.relativeIndex !== undefined ? condition.ref.relativeIndex : null,
        context: condition.ref.context || '',
        regex: condition.ref.regex || '',
        valueRegex: condition.ref.valueRegex || condition.ref.value_regex || '',
      });
    }

    // Map nested conditions (for CHILDREN, OR, NOT)
    if (Array.isArray(condition.conditions)) {
      const conditionsArray = conditionGroup.get('conditions') as FormArray;
      condition.conditions.forEach((nestedCondition: any) => {
        conditionsArray.push(this.mapJsonConditionToForm(nestedCondition));
      });
    }

    // Map target array (for CHILDREN, LIST)
    if (Array.isArray(condition.target)) {
      const targetArray = conditionGroup.get('target') as FormArray;
      condition.target.forEach((targetCondition: any) => {
        targetArray.push(this.mapJsonConditionToForm(targetCondition));
      });
    }

    // Map single condition (for NOT)
    if (condition.condition && typeof condition.condition === 'object') {
      const conditionControl = conditionGroup.get('condition') as FormGroup;
      if (conditionControl) {
        const mappedCondition = this.mapJsonConditionToForm(condition.condition);
        // Remove all existing controls and add the mapped condition's controls
        Object.keys(conditionControl.controls).forEach((key) => {
          conditionControl.removeControl(key);
        });
        Object.keys(mappedCondition.controls).forEach((key) => {
          conditionControl.addControl(key, mappedCondition.get(key)!);
        });
      }
    }

    return conditionGroup;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormArray) {
        control.controls.forEach((arrayControl) => {
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          } else {
            arrayControl.markAsTouched();
          }
        });
      } else if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private getMissingRequiredFields(formGroup: FormGroup, prefix: string = ''): string[] {
    const missingFields: string[] = [];

    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      const fieldPath = prefix ? `${prefix}.${key}` : key;

      if (control instanceof FormArray) {
        control.controls.forEach((arrayControl, index) => {
          const arrayPath = `${fieldPath}[${index}]`;
          if (arrayControl instanceof FormGroup) {
            missingFields.push(...this.getMissingRequiredFields(arrayControl, arrayPath));
          } else if (arrayControl.invalid && arrayControl.hasError('required')) {
            // Format the field name for better readability
            const readableName = this.getReadableFieldName('', arrayPath);
            missingFields.push(readableName);
          }
        });
      } else if (control instanceof FormGroup) {
        // Handle nested FormGroups (like ref, condition, subLayouts)
        if (key === 'subLayouts') {
          // subLayouts is a FormGroup containing FormArrays
          Object.keys(control.controls).forEach((subLayoutName) => {
            const subLayoutArray = control.get(subLayoutName) as FormArray;
            if (subLayoutArray) {
              subLayoutArray.controls.forEach((subLayoutCondition, conditionIndex) => {
                if (subLayoutCondition instanceof FormGroup) {
                  missingFields.push(...this.getMissingRequiredFields(
                    subLayoutCondition,
                    `${fieldPath}.${subLayoutName}[${conditionIndex}]`
                  ));
                }
              });
            }
          });
        } else {
          missingFields.push(...this.getMissingRequiredFields(control, fieldPath));
        }
      } else if (control && control.invalid && control.hasError('required')) {
        // Format field names for better readability
        const readableName = this.getReadableFieldName(key, fieldPath);
        missingFields.push(readableName);
      }
    });

    return missingFields;
  }

  private getReadableFieldName(key: string, fullPath: string): string {
    // Map technical field names to user-friendly names
    const fieldNameMap: { [key: string]: string } = {
      'version': 'Package Revision',
      'package': 'Package',
      'minVersion': 'Min Package Revision',
      'maxVersion': 'Max Package Revision',
      'layoutType': 'Layout Type',
      'priority': 'Priority',
      'alertProbability': 'Alert Probability',
      'type': 'Condition Type',
      'previousLayouts': 'Previous Layout',
    };

    // Root-level version (system-managed) should still be named "Version" if ever shown.
    if (fullPath === 'version') {
      return 'Version';
    }

    // Handle different path patterns
    // Pattern: apps[0].package -> "App 1 - Package"
    if (fullPath.startsWith('apps[')) {
      const match = fullPath.match(/apps\[(\d+)\]\.(\w+)/);
      if (match) {
        const [, index, fieldName] = match;
        const readableField = fieldNameMap[fieldName] || fieldName;
        return `App ${parseInt(index) + 1} - ${readableField}`;
      }
    }

    // Pattern: layouts[0].previousLayouts[0] -> "Layout 1 - Previous Layout 1"
    // Check this BEFORE the general layouts pattern to avoid incorrect matching
    if (fullPath.includes('.previousLayouts[')) {
      const match = fullPath.match(/layouts\[(\d+)\]\.previousLayouts\[(\d+)\]/);
      if (match) {
        const [, layoutIndex, prevIndex] = match;
        return `Layout ${parseInt(layoutIndex) + 1} - Previous Layout ${parseInt(prevIndex) + 1}`;
      }
    }

    // Pattern: layouts[0].conditions[1].type -> "Layout 1 - Condition 2 - Condition Type"
    // Check this BEFORE the general layouts pattern to avoid incorrect matching
    if (fullPath.includes('.conditions[')) {
      const match = fullPath.match(/layouts\[(\d+)\]\.conditions\[(\d+)\]\.(\w+)/);
      if (match) {
        const [, layoutIndex, conditionIndex, fieldName] = match;
        const readableField = fieldNameMap[fieldName] || fieldName;
        return `Layout ${parseInt(layoutIndex) + 1} - Condition ${parseInt(conditionIndex) + 1} - ${readableField}`;
      }
    }

    // Pattern: layouts[0].layoutType -> "Layout 1 - Layout Type"
    // This should be checked last as it's the most general pattern
    if (fullPath.startsWith('layouts[')) {
      const match = fullPath.match(/layouts\[(\d+)\]\.(\w+)/);
      if (match) {
        const [, index, fieldName] = match;
        // Skip if it's previousLayouts or conditions (already handled above)
        if (fieldName === 'previousLayouts' || fieldName === 'conditions') {
          return fullPath; // Fallback to original path if pattern didn't match above
        }
        const readableField = fieldNameMap[fieldName] || fieldName;
        return `Layout ${parseInt(index) + 1} - ${readableField}`;
      }
    }

    // Pattern: layouts[0].subLayouts.QuestionItem[0].type -> "Layout 1 - Sub Layout 'QuestionItem' - Condition 1 - Condition Type"
    if (fullPath.includes('.subLayouts.')) {
      const match = fullPath.match(/layouts\[(\d+)\]\.subLayouts\.(\w+)\[(\d+)\]\.(\w+)/);
      if (match) {
        const [, layoutIndex, subLayoutName, conditionIndex, fieldName] = match;
        const readableField = fieldNameMap[fieldName] || fieldName;
        return `Layout ${parseInt(layoutIndex) + 1} - Sub Layout '${subLayoutName}' - Condition ${parseInt(conditionIndex) + 1} - ${readableField}`;
      }
    }

    // For root level fields
    if (fieldNameMap[key]) {
      return fieldNameMap[key];
    }

    // Fallback to the key itself
    return key;
  }

  get isBusy(): boolean {
    return this.isSubmitting;
  }

  private isLayoutSuccessResponse(res: any): boolean {
    // Check for explicit success flag
    if (res?.success === true) {
      return true;
    }
    // Check for status code in 200-299 range
    if (res?.statusCode >= 200 && res?.statusCode < 300) {
      return true;
    }
    // Check if response is the Layout object directly (has _id and layouts array)
    if (res?._id && Array.isArray(res?.layouts) && res.layouts.length > 0) {
      return true;
    }
    // Check if response has content without error
    if (res?.content && !res?.errorMessage) {
      return true;
    }
    return false;
  }

  toggleSection(section: string): void {
    if (this.isBusy) return;
    this.collapsedSections[section] = !this.collapsedSections[section];
  }

  toggleApp(index: number): void {
    if (this.isBusy) return;
    this.collapsedApps[index] = !this.collapsedApps[index];
  }

  toggleLayout(index: number): void {
    if (this.isBusy) return;
    const isNowCollapsed = !this.collapsedLayouts[index];
    this.collapsedLayouts[index] = isNowCollapsed;

    // When a layout is collapsed, its related sections should also be collapsed
    if (isNowCollapsed) {
      this.collapsedPreviousLayouts[index] = true;
      this.collapsedSubLayouts[index] = true;
      const conditionsKey = `conditions-${index}`;
      this.collapsedConditions[conditionsKey] = true;

      // Also collapse all individual conditions within this layout
      const conditionsArray = this.getConditionsFormArray(index);
      for (let j = 0; j < conditionsArray.length; j++) {
        const conditionKey = `${index}-${j}`;
        this.collapsedConditions[conditionKey] = true;
      }
    }
  }

  toggleConditions(layoutIndex: number): void {
    if (this.isBusy) return;
    const key = `conditions-${layoutIndex}`;
    this.collapsedConditions[key] = !this.collapsedConditions[key];
  }

  toggleCondition(layoutIndex: number, conditionIndex: number): void {
    if (this.isBusy) return;
    const key = `${layoutIndex}-${conditionIndex}`;
    this.collapsedConditions[key] = !this.collapsedConditions[key];
  }

  togglePreviousLayouts(layoutIndex: number): void {
    if (this.isBusy) return;
    this.collapsedPreviousLayouts[layoutIndex] = !this.collapsedPreviousLayouts[layoutIndex];
  }

  isAppCollapsed(index: number): boolean {
    return this.collapsedApps[index] === true;
  }

  isLayoutCollapsed(index: number): boolean {
    return this.collapsedLayouts[index] === true;
  }

  isConditionsCollapsed(layoutIndex: number): boolean {
    const key = `conditions-${layoutIndex}`;
    return this.collapsedConditions[key] === true;
  }

  isConditionCollapsed(layoutIndex: number, conditionIndex: number): boolean {
    const key = `${layoutIndex}-${conditionIndex}`;
    return this.collapsedConditions[key] === true;
  }

  isPreviousLayoutsCollapsed(layoutIndex: number): boolean {
    return this.collapsedPreviousLayouts[layoutIndex] === true;
  }

  // Helper method to serialize conditions recursively
  private serializeConditions(conditions: any[]): any[] {
    return conditions.map((condition: any) => {
      const serialized: any = {
        type: condition.type,
      };

      // Add optional fields only if they have values
      if (condition.description) serialized.description = condition.description;
      if (condition.min !== null && condition.min !== undefined) serialized.min = Number(condition.min);
      if (condition.max !== null && condition.max !== undefined) serialized.max = Number(condition.max);
      if (condition.values && condition.values.length > 0) serialized.values = condition.values.filter((v: string) => v?.trim());
      if (condition.index !== null && condition.index !== undefined) serialized.index = Number(condition.index);
      if (condition.indices && condition.indices.length > 0) serialized.indices = condition.indices.map((i: any) => Number(i)).filter((i: number) => !isNaN(i));
      if (condition.exact !== undefined) serialized.exact = Boolean(condition.exact);
      if (condition.className) serialized.className = condition.className;
      if (condition.classNames && condition.classNames.length > 0) serialized.classNames = condition.classNames.filter((cn: string) => cn?.trim());
      if (condition.regex) serialized.regex = condition.regex;
      if (condition.reverseSearch !== undefined) serialized.reverseSearch = Boolean(condition.reverseSearch);
      if (condition.item) serialized.item = condition.item;
      if (condition.items && condition.items.length > 0) serialized.items = condition.items.filter((item: string) => item?.trim());
      if (condition.itemClassName) serialized.itemClassName = condition.itemClassName;
      if (condition.minSize !== null && condition.minSize !== undefined) serialized.minSize = Number(condition.minSize);
      if (condition.step !== null && condition.step !== undefined) serialized.step = Number(condition.step);
      if (condition.layout) serialized.layout = condition.layout;
      if (condition.readOnly) serialized.readOnly = true;
      if (condition.ignoreConditions && condition.ignoreConditions.length > 0) serialized.ignoreConditions = condition.ignoreConditions.map((ic: any) => Number(ic)).filter((ic: number) => !isNaN(ic));
      if (condition.targetConditionMatching) serialized.targetConditionMatching = true;
      if (condition.toIndex !== null && condition.toIndex !== undefined) serialized.toIndex = Number(condition.toIndex);
      if (condition.fromIndex !== null && condition.fromIndex !== undefined) serialized.fromIndex = Number(condition.fromIndex);
      if (condition.childCount) {
        const ccMin = condition.childCount.min;
        const ccMax = condition.childCount.max;
        if ((ccMin !== null && ccMin !== undefined) || (ccMax !== null && ccMax !== undefined)) {
          serialized.childCount = {};
          if (ccMin !== null && ccMin !== undefined) serialized.childCount.min = Number(ccMin);
          if (ccMax !== null && ccMax !== undefined) serialized.childCount.max = Number(ccMax);
        }
      }

      // Handle nested structures
      if (condition.target && condition.target.length > 0) {
        serialized.target = condition.target.map((t: any) => this.serializeConditions([t])[0] || t);
      }
      if (condition.conditions && condition.conditions.length > 0) {
        serialized.conditions = this.serializeConditions(condition.conditions);
      }
      if (condition.condition && Object.keys(condition.condition).length > 0) {
        serialized.condition = this.serializeConditions([condition.condition])[0];
      }
      if (
        condition.ref &&
        (
          condition.ref.tag ||
          condition.ref.index !== null ||
          condition.ref.relativeIndex !== null ||
          condition.ref.context ||
          condition.ref.regex ||
          condition.ref.valueRegex
        )
      ) {
        serialized.ref = {};
        if (condition.ref.tag) serialized.ref.tag = condition.ref.tag;
        if (condition.ref.index !== null && condition.ref.index !== undefined) serialized.ref.index = Number(condition.ref.index);
        if (condition.ref.relativeIndex !== null && condition.ref.relativeIndex !== undefined) serialized.ref.relativeIndex = Number(condition.ref.relativeIndex);
        if (condition.ref.context) serialized.ref.context = condition.ref.context;
        if (condition.ref.regex) serialized.ref.regex = condition.ref.regex;
        if (condition.ref.valueRegex) serialized.ref.valueRegex = condition.ref.valueRegex;
      }

      return serialized;
    });
  }

  // Helper method to serialize subLayouts
  private serializeSubLayouts(subLayouts: any): any {
    const serialized: any = {};
    Object.keys(subLayouts).forEach((key) => {
      if (subLayouts[key] && Array.isArray(subLayouts[key]) && subLayouts[key].length > 0) {
        serialized[key] = this.serializeConditions(subLayouts[key]);
      }
    });
    return Object.keys(serialized).length > 0 ? serialized : undefined;
  }

  // ---- IOHC-1723: carry-through fields + workflow ("flows") JSON editor ----

  // Set the carried-through layout fields and the workflow array (+ its JSON-editor text)
  // from a layout object, for both the upload and edit populate paths.
  /**
   * Populate a layout's previousLayouts controls from source data (IOHB-1724).
   * Authored values are kept verbatim — an explicit "None" is a valid LayoutType sentinel meaning
   * "no previous layout detected". When the source authors none the array is left EMPTY so the
   * field is omitted on save: Android skips the previous-layout constraint only when it is absent.
   */
  private populatePreviousLayouts(layoutGroup: FormGroup, layout: any): void {
    const previousLayoutsArray = layoutGroup.get('previousLayouts') as FormArray;
    while (previousLayoutsArray.length > 0) {
      previousLayoutsArray.removeAt(0);
    }
    if (!Array.isArray(layout?.previousLayouts)) {
      return;
    }
    layout.previousLayouts.forEach((prevLayout: string) => {
      const value = prevLayout?.trim();
      if (value) {
        previousLayoutsArray.push(this.formBuilder.control(value, [Validators.required]));
      }
    });
  }

  private applyCarryThroughFields(layoutGroup: FormGroup, layout: any, index: number): void {
    layoutGroup.get('previousScreenContexts')?.setValue(
      Array.isArray(layout?.previousScreenContexts) ? layout.previousScreenContexts : null,
    );
    layoutGroup.get('supportedOverlays')?.setValue(
      Array.isArray(layout?.supportedOverlays) ? layout.supportedOverlays : null,
    );
    layoutGroup.get('supportedHighlights')?.setValue(
      Array.isArray(layout?.supportedHighlights) ? layout.supportedHighlights : null,
    );
    const wf = Array.isArray(layout?.workflow) ? layout.workflow : null;
    layoutGroup.get('workflow')?.setValue(wf);
    this.workflowJsonText[index] = wf ? JSON.stringify(wf, null, 2) : '';
    this.workflowJsonError[index] = '';
    this.collapsedWorkflow[index] = true;
  }

  toggleWorkflow(layoutIndex: number): void {
    if (this.isBusy) return;
    this.collapsedWorkflow[layoutIndex] = !this.collapsedWorkflow[layoutIndex];
  }

  isWorkflowCollapsed(layoutIndex: number): boolean {
    return this.collapsedWorkflow[layoutIndex] === true;
  }

  // Parse the workflow JSON textarea into the reactive control; record a per-layout error
  // (which blocks save) when the JSON is malformed or not an array.
  onWorkflowJsonChange(layoutIndex: number, text: string): void {
    this.workflowJsonText[layoutIndex] = text ?? '';
    const trimmed = (text ?? '').trim();
    const control = this.layoutsFormArray.at(layoutIndex)?.get('workflow');
    if (!trimmed) {
      this.workflowJsonError[layoutIndex] = '';
      control?.setValue(null);
      return;
    }
    try {
      const parsed = JSON.parse(trimmed);
      if (!Array.isArray(parsed)) {
        this.workflowJsonError[layoutIndex] = 'Workflow must be a JSON array of pipelines / actions.';
        return;
      }
      this.workflowJsonError[layoutIndex] = '';
      control?.setValue(parsed);
    } catch (e: any) {
      this.workflowJsonError[layoutIndex] = 'Invalid JSON: ' + (e?.message || e);
    }
  }

  hasWorkflowErrors(): boolean {
    // Only consider errors for indices that still map to a live layout, so a stale error left
    // by a removed/absent layout can never permanently block Save.
    return Object.keys(this.workflowJsonError).some(
      (k) => Number(k) < this.layoutsFormArray.length && !!this.workflowJsonError[Number(k)],
    );
  }

  // Clear all per-index workflow-editor state. Called from initForm() and both populate paths
  // so stale JSON text / errors / collapse flags never leak across dialogs, sessions, or a
  // shrinking layout count.
  private resetWorkflowEditorState(): void {
    this.collapsedWorkflow = {};
    this.workflowJsonText = {};
    this.workflowJsonError = {};
  }

  // Normalise an editable string[] field (previousScreenContexts / supportedOverlays /
  // supportedHighlights) for the payload; returns undefined when empty so it is omitted.
  private cleanStringArray(arr: any): string[] | undefined {
    if (!Array.isArray(arr)) return undefined;
    const cleaned = arr
      .map((s) => (typeof s === 'string' ? s.trim() : s))
      .filter((s) => s !== '' && s !== null && s !== undefined);
    return cleaned.length > 0 ? cleaned : undefined;
  }

  // Comma-separated text <-> string[] helpers for the carried-through array fields.
  getStringArrayText(layoutIndex: number, field: string): string {
    const val = this.layoutsFormArray.at(layoutIndex)?.get(field)?.value;
    return Array.isArray(val) ? val.join(', ') : '';
  }

  onStringArrayTextChange(layoutIndex: number, field: string, text: string): void {
    const arr = (text ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s !== '');
    this.layoutsFormArray.at(layoutIndex)?.get(field)?.setValue(arr.length > 0 ? arr : null);
  }

  onDelete(layout: any): void {
    if (!layout?._id && layout?.id === undefined) {
      return;
    }

    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this layout?',
      header: 'Delete Layout',
      icon: 'pi pi-trash',
      acceptButtonStyleClass: 'btn-delete',
      accept: () => {
        this.progressBarService.setProgressBarVisibility(true);
        const layoutId = layout._id || layout.id;

        this.subscriptions.push(
          this.layoutService.deleteLayout(layoutId).subscribe({
            next: (res) => {
              // Handle null/undefined/empty response - DELETE API returns 200 with empty body
              // If we reach the next callback, it means the request was successful (200 status)
              if (res === null || res === undefined || (typeof res === 'object' && Object.keys(res || {}).length === 0)) {
                // Empty response with 200 status means success for DELETE operations
                this.messageHandlerService.generateToastMessage(
                  'success',
                  'Success',
                  'Layout deleted successfully'
                );
                this.getLayouts();
                this.progressBarService.setProgressBarVisibility(false);
                return;
              }

              if (this.isLayoutListSuccessResponse(res)) {
                this.messageHandlerService.generateToastMessage(
                  'success',
                  'Success',
                  'Layout deleted successfully'
                );
                this.getLayouts();
              } else {
                this.messageHandlerService.generateToastMessage(
                  'error',
                  ERROR_MESSAGES.COMMUNICATION,
                  res?.errorMessage || res?.message || 'Failed to delete layout'
                );
              }
              this.progressBarService.setProgressBarVisibility(false);
            },
            error: (error) => {
              const errorMessage = error?.errorMessage || error?.message || error;

              // Swallow technical RxJS error caused by interceptor returning `null`
              // ("You provided 'null' where a stream was expected"), since the
              // ErrorInterceptor already showed a user-facing toast for that case.
              if (
                typeof errorMessage === 'string' &&
                errorMessage.includes("You provided 'null' where a stream was expected")
              ) {
                this.progressBarService.setProgressBarVisibility(false);
                return;
              }

              // Check if error status is 403/400/500 - interceptor already handled it
              if (error?.status === 403 || error?.status === 400 || error?.status === 500) {
                this.progressBarService.setProgressBarVisibility(false);
                return;
              }

              this.progressBarService.setProgressBarVisibility(false);
              this.messageHandlerService.generateToastMessage(
                'error',
                ERROR_MESSAGES.COMMUNICATION,
                errorMessage
              );
            },
          })
        );
      },
    });
  }

  onRowClick(layout: any): void {
    // Open edit dialog with the selected layout data
    this.openEditDialog(layout);
  }

  onEditLayout(event: Event, layout: any): void {
    event.stopPropagation();
    this.openEditDialog(layout);
  }

  onDownloadLayoutJson(event: Event, layout: any): void {
    event.stopPropagation();
    this.downloadJson(layout, this.getLayoutDownloadFileName(layout));
  }

  private getLayoutDownloadFileName(layout: any): string {
    const id = layout?._id ?? layout?.id ?? layout?.version ?? 'layout';
    const raw = `layout-${String(id)}.json`;
    // Keep filename safe for most OSes.
    return raw.replace(/[\\/:*?"<>|]+/g, '-');
  }

  private downloadJson(data: unknown, filename: string): void {
    const json = JSON.stringify(data ?? {}, null, 2);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  openEditDialog(layout: any): void {
    try {
      this.isEditMode = true;
      this.selectedLayout = layout;

      if (!this.layoutForm) {
        this.initForm();
      }

      if (!this.layoutForm) {
        return;
      }

      // Reset file upload state (not needed for edit mode, but keep consistent)
      this.selectedJsonFile = null;
      this.uploadedFileName = null;
      this.isFileUploaded = true; // Enable form fields for editing
      this.resetUploadState();

      // Populate form with layout data
      this.populateFormFromLayoutData(layout);

      this.dialogVisible = true;
    } catch (error) {
      console.error('Error opening edit dialog:', error);
      this.messageHandlerService.generateToastMessage(
        'error',
        'Error',
        'Failed to open form for editing. Please try again.'
      );
    }
  }

  private populateFormFromLayoutData(layoutData: any): void {
    if (!this.layoutForm || !layoutData) {
      return;
    }

    try {
      // Set root level properties
      this.layoutForm.patchValue({
        version: layoutData.version || 1,
        companyId: layoutData.companyId || '',
        agencyId: layoutData.agencyId || '',
        userId: layoutData.userId || '',
      });

      // Clear existing arrays
      this.resetWorkflowEditorState(); // IOHC-1723: drop stale per-index workflow-editor state
      const appsFormArray = this.appsFormArray;
      const layoutsFormArray = this.layoutsFormArray;

      while (appsFormArray.length > 0) {
        appsFormArray.removeAt(0);
      }
      while (layoutsFormArray.length > 0) {
        layoutsFormArray.removeAt(0);
      }

      // Populate apps
      if (Array.isArray(layoutData.apps) && layoutData.apps.length > 0) {
        layoutData.apps.forEach((app: any, index: number) => {
          appsFormArray.push(this.formBuilder.group({
            package: [app.package || '', [Validators.required]],
            version: [app.version || '', [Validators.required]],
            minVersion: [app.minVersion || '', [Validators.required]],
            maxVersion: [app.maxVersion || '', [Validators.required]],
          }));
          this.collapsedApps[index] = index !== 0; // Only first app expanded
        });
      } else {
        appsFormArray.push(this.createAppFormGroup());
        this.collapsedApps[0] = false;
      }

      // Populate layouts
      if (Array.isArray(layoutData.layouts) && layoutData.layouts.length > 0) {
        layoutData.layouts.forEach((layout: any, index: number) => {
          const layoutGroup = this.createLayoutFormGroup();

          // ... (rest of the mapping logic remains the same)

          // Set basic properties
          layoutGroup.patchValue({
            layoutType: layout.layoutType || 'grid',
            priority: layout.priority || 1,
            critical: layout.critical || false,
            alertProbability: layout.alertProbability || 0.9,
            contextType: layout.contextType || '',
            screenContext: layout.screenContext || '',
          });

          // Populate conditions
          const conditionsArray = layoutGroup.get('conditions') as FormArray;
          while (conditionsArray.length > 0) {
            conditionsArray.removeAt(0);
          }
          if (Array.isArray(layout.conditions) && layout.conditions.length > 0) {
            layout.conditions.forEach((condition: any) => {
              conditionsArray.push(this.mapJsonConditionToForm(condition));
            });
          }

          this.populatePreviousLayouts(layoutGroup, layout);

          // Populate subLayouts
          const subLayoutsGroup = layoutGroup.get('subLayouts') as FormGroup;
          if (layout.subLayouts && typeof layout.subLayouts === 'object') {
            Object.keys(layout.subLayouts).forEach((subLayoutName) => {
              const subLayoutConditions = layout.subLayouts[subLayoutName];
              if (Array.isArray(subLayoutConditions)) {
                const conditionFormGroups = subLayoutConditions.map((condition: any) =>
                  this.mapJsonConditionToForm(condition)
                );
                const subLayoutArray = this.formBuilder.array(conditionFormGroups);
                subLayoutsGroup.addControl(subLayoutName, subLayoutArray);
              }
            });
          }

          // IOHC-1723: carry-through fields + workflow ("flows"), preserved verbatim.
          this.applyCarryThroughFields(layoutGroup, layout, index);

          layoutsFormArray.push(layoutGroup);

          // Initialize collapsed states for new layout
          this.collapsedLayouts[index] = index !== 0; // Only first layout expanded
          this.collapsedPreviousLayouts[index] = true; // Related sections collapsed by default
          this.collapsedSubLayouts[index] = true; // Related sections collapsed by default
          const conditionsKey = `conditions-${index}`;
          this.collapsedConditions[conditionsKey] = true; // Related sections collapsed by default
        });
      } else {
        layoutsFormArray.push(this.createLayoutFormGroup());
        this.collapsedLayouts[0] = false;
      }

      // Collapse top-level sections except Apps and Layouts
      this.collapsedSections = {
        apps: false,
        layouts: false,
        version: false,
      };
    } catch (error) {
      console.error('Error populating form from layout data:', error);
      this.messageHandlerService.generateToastMessage(
        'error',
        'Error',
        'Failed to populate form with layout data'
      );
    }
  }
}

